import { Clock, Users, MapPin, GraduationCap } from "lucide-react";
import Page from "@/components/Page";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import ContactBanner from "@/components/ContactBanner";
import EditorialMarquee from "@/components/Marquee";
import { useContent } from "@/content/ContentContext";

const ICONS = [Clock, Users, MapPin, GraduationCap];

const Training = () => {
  const { content } = useContent();
  const { training } = content;
  return (
    <Page data-testid="training-page">
      <PageHero
        id="training"
        overline={training.heroOverline}
        lines={training.heroLines}
        intro={training.heroIntro}
      />

      <section className="bg-cream" data-testid="training-formats">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-28">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.32em] uppercase text-gold-muted mb-5">{training.formatsOverline}</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-medium text-pine leading-tight max-w-2xl">
              {training.formatsTitle}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px bg-line border border-line sm:grid-cols-2 lg:grid-cols-4">
            {training.formats.map((f, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <Reveal key={i} delay={i * 0.07}>
                  <div className="group h-full bg-white px-7 py-9 transition-colors duration-300 hover:bg-pine" data-testid={`training-format-${i + 1}`}>
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-pine text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-pine">
                      <Icon size={18} />
                    </span>
                    <h3 className="mt-5 font-serif text-xl font-medium text-pine transition-colors duration-300 group-hover:text-cream">
                      {f.t}
                    </h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-ink/65 transition-colors duration-300 group-hover:text-cream/70">
                      {f.d}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <EditorialMarquee />

      <section className="bg-white border-b border-line" data-testid="training-workshops">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.32em] uppercase text-gold-muted mb-5">{training.workshopsOverline}</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-medium text-pine leading-tight max-w-2xl">
              {training.workshopsTitle}
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70">
              {training.workshopsText}
            </p>
          </Reveal>

          <div className="mt-14 grid gap-5 md:grid-cols-2">
            {training.workshops.map((w, i) => (
              <Reveal key={i} delay={(i % 2) * 0.08}>
                <article
                  className="group h-full border border-line bg-cream/50 px-8 py-9 transition-colors duration-300 hover:border-gold/60"
                  data-testid={`workshop-card-${i + 1}`}
                >
                  <span className="font-serif text-lg text-gold-muted">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-serif text-2xl font-medium text-pine leading-snug">{w.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/65">{w.d}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ContactBanner overline={training.banner.overline} title={training.banner.title} blurb={training.banner.blurb} />
    </Page>
  );
};

export default Training;
